var IpcRules = require('../ipcRules');
var IPCSmdFootprintCalculator = require('./calculator');
var ComponentAttribute = require('./componentAttribute');
var IpcDataModel = require("../index.js");

/**
 * Represent Component class.
 * This is a helper class and it is used to calculate footprint pad sizes and center to center distance using IPC parameters.
 * @constructor
 * @param {data} data Persistent data model for SMD pad replicator
 */
var Component = function(data) {
	//associated datamodel
	this.data = data;

	//lead to lead span
	//these params are updated by the datamodel
	this.leadToLeadSpanMin = 0;
	this.leadToLeadSpanMax = 0;

	//pad width and height
	//these params are updated by the datamodel and are mainly used for calculating different size pads in components
	this.padWidthMin = 0;
	this.padWidthMax = 0;
	this.padHeightMin = 0;
	this.padHeightMax = 0;

	//number of sides
	this.sideCount = 2;

	//number of pads per side
	this.padsPerSide = [];

	//actual pad dimensions for IPC generated footprint
	this.X = 0; //pad width
	this.Y = 0; //pad height
	this.C = 0; //pad center to center distance

	//IPC goals for toe, heel and side
	this.toeGoal = 0;
	this.heelGoal = 0;
	this.sideGoal = 0;
	this.toeMin = 0;
	this.toeMax = 0;
	this.heelMin = 0;
	this.heelMax = 0;
	this.sideMin = 0;
	this.sideMax = 0;

	//initialize the component
	this.init();
};

module.exports = Component;

/**
 * Reset Component to use values from existing datamodel.
 */
Component.prototype.reset = function() {
	var self = this;

	self.leadToLeadSpanMin = self.data.horizontalLeadToLeadSpanMin();
	self.leadToLeadSpanMax = self.data.horizontalLeadToLeadSpanMax();
	self.padWidthMin = self.data.padWidthMin();
	self.padWidthMax = self.data.padWidthMax();
	self.padHeightMin = self.data.padHeightMin();
	self.padHeightMax = self.data.padHeightMax();
};

/**
 * Set Component pad's toe, heel and side goals.
 */
Component.prototype.setComponentPadGoals = function() {
	var self = this;

	//update component pad goals defined in IPC Rules
	//refer tables 3-2 to 3-22 in IPC-7351(B) doc for details
	switch(self.data.constructor) {
		case IpcDataModel.SoicSopData:
		case IpcDataModel.CfpData:
		case IpcDataModel.QfpData:
		case IpcDataModel.CqfpData:
		case IpcDataModel.Sot23Data:
		case IpcDataModel.Sot223Data:
		case IpcDataModel.Sot143Data:
		case IpcDataModel.ToDpakData:
			if(self.data.verticalPinPitch() > IpcRules.padGoal.gullWing.pitchTh) {
				self.toeGoal = IpcRules.padGoal.gullWing.toeFilletMaxMedMinGT[self.data.densityLevel()];
				self.heelGoal = IpcRules.padGoal.gullWing.heelFilletMaxMedMinGT[self.data.densityLevel()];
				self.sideGoal = IpcRules.padGoal.gullWing.sideFilletMaxMedMinGT[self.data.densityLevel()];
			} else {
				self.toeGoal = IpcRules.padGoal.gullWing.toeFilletMaxMedMinLTE[self.data.densityLevel()];
				self.heelGoal = IpcRules.padGoal.gullWing.heelFilletMaxMedMinLTE[self.data.densityLevel()];
				self.sideGoal = IpcRules.padGoal.gullWing.sideFilletMaxMedMinLTE[self.data.densityLevel()];
			}
		break;

		case IpcDataModel.SodData:
			self.toeGoal = IpcRules.padGoal.gullWing.toeFilletMaxMedMinGT[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.gullWing.heelFilletMaxMedMinGT[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.gullWing.sideFilletMaxMedMinGT[self.data.densityLevel()];
		break;

		case IpcDataModel.QfnData:
		case IpcDataModel.SonData:
			self.toeGoal = IpcRules.padGoal.noLead.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.noLead.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.noLead.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.ChipData:
			if(self.data.bodyWidthMax() >= IpcRules.padGoal.chip.bodyLengthTh) {
				self.toeGoal = IpcRules.padGoal.chip.toeFilletMaxMedMinGTE[self.data.densityLevel()];
				self.heelGoal = IpcRules.padGoal.chip.heelFilletMaxMedMinGTE[self.data.densityLevel()];
				self.sideGoal = IpcRules.padGoal.chip.sideFilletMaxMedMinGTE[self.data.densityLevel()];
			} else {
				self.toeGoal = IpcRules.padGoal.chip.toeFilletMaxMedMinLT[self.data.densityLevel()];
				self.heelGoal = IpcRules.padGoal.chip.heelFilletMaxMedMinLT[self.data.densityLevel()];
				self.sideGoal = IpcRules.padGoal.chip.sideFilletMaxMedMinLT[self.data.densityLevel()];
			}
		break;

		case IpcDataModel.MoldedBodyData:
			self.toeGoal = IpcRules.padGoal.moldedBody.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.moldedBody.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.moldedBody.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.SotflData:
		case IpcDataModel.SodflData:
			self.toeGoal = IpcRules.padGoal.smallOutlineFlatLead.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.smallOutlineFlatLead.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.smallOutlineFlatLead.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.Dfn2Data:
		case IpcDataModel.Dfn3Data:
		case IpcDataModel.Dfn4Data:
			self.toeGoal = IpcRules.padGoal.dfn.periphery[self.data.densityLevel()];
			self.sideGoal = self.heelGoal = self.toeGoal;
		break;

		case IpcDataModel.AluminumElectrolyticData:
		case IpcDataModel.CrystalData:
			if(self.data.bodyHeightMax() >= IpcRules.padGoal.aluminumElectrolytic.bodyHeightTh) {
				self.toeGoal = IpcRules.padGoal.aluminumElectrolytic.toeFilletMaxMedMinGTE[self.data.densityLevel()];
				self.heelGoal = IpcRules.padGoal.aluminumElectrolytic.heelFilletMaxMedMinGTE[self.data.densityLevel()];
				self.sideGoal = IpcRules.padGoal.aluminumElectrolytic.sideFilletMaxMedMinGTE[self.data.densityLevel()];
			} else {
				self.toeGoal = IpcRules.padGoal.aluminumElectrolytic.toeFilletMaxMedMinLT[self.data.densityLevel()];
				self.heelGoal = IpcRules.padGoal.aluminumElectrolytic.heelFilletMaxMedMinLT[self.data.densityLevel()];
				self.sideGoal = IpcRules.padGoal.aluminumElectrolytic.sideFilletMaxMedMinLT[self.data.densityLevel()];
			}
		break;
		
		case IpcDataModel.ChipArrayTwoSideFlatConcaveData:
		case IpcDataModel.ChipArrayTwoSideConvexData:
		case IpcDataModel.ChipArrayFourSideFlatConcaveData:
			self.toeGoal = IpcRules.padGoal.chipArray.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.chipArray.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.chipArray.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.MelfData:
			self.toeGoal = IpcRules.padGoal.melf.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.melf.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.melf.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.PlccData:
			self.toeGoal = IpcRules.padGoal.plcc.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.plcc.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.plcc.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.OscillatorCornerConcaveData:
			self.toeGoal = IpcRules.padGoal.oscillator.cornerConcave.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.oscillator.cornerConcave.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.oscillator.cornerConcave.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.OscillatorJLeadData:
			self.toeGoal = IpcRules.padGoal.oscillator.jLead.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.oscillator.jLead.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.oscillator.jLead.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.OscillatorLBendData:
			self.toeGoal = IpcRules.padGoal.oscillator.lBend.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.oscillator.lBend.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.oscillator.lBend.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		case IpcDataModel.SojData:
			self.toeGoal = IpcRules.padGoal.soj.toeFilletMaxMedMin[self.data.densityLevel()];
			self.heelGoal = IpcRules.padGoal.soj.heelFilletMaxMedMin[self.data.densityLevel()];
			self.sideGoal = IpcRules.padGoal.soj.sideFilletMaxMedMin[self.data.densityLevel()];
		break;

		default:
		break;
	}
};

/**
 * Initialize the component with side count and number of pads in each side.
 */
Component.prototype.init = function() {
	var self = this;

	//calculate number of pads in each side
	//if the total number of pads is odd, pads per side is rounded off to next higher integer
	//Note: this is a temporary fix until inspector UI is improved
	switch(self.data.constructor) {
		case IpcDataModel.SoicSopData:
		case IpcDataModel.CfpData:
		case IpcDataModel.ChipArrayTwoSideFlatConcaveData:
		case IpcDataModel.ChipArrayTwoSideConvexData:
		case IpcDataModel.OscillatorCornerConcaveData:
		case IpcDataModel.OscillatorJLeadData:
		case IpcDataModel.OscillatorLBendData:
		case IpcDataModel.SojData:
		case IpcDataModel.SonData:
			self.sideCount = 2;

			self.padsPerSide[0] = Math.ceil(self.data.horizontalPadCount() / self.sideCount);
			self.padsPerSide[1] = self.padsPerSide[0];

		break;

		case IpcDataModel.ChipData:
		case IpcDataModel.SodData:
		case IpcDataModel.MoldedBodyData:
		case IpcDataModel.SodflData:
		case IpcDataModel.Dfn2Data:
		case IpcDataModel.AluminumElectrolyticData:
		case IpcDataModel.CrystalData:
		case IpcDataModel.MelfData:
			self.sideCount = 2;

			self.padsPerSide[0] = 1;
			self.padsPerSide[1] = 1;
		break;

		case IpcDataModel.QfpData:
		case IpcDataModel.CqfpData:
		case IpcDataModel.QfnData:
		case IpcDataModel.PlccData:
			self.sideCount = 4;

			self.padsPerSide[0] = Math.ceil(self.data.horizontalPadCount());
			self.padsPerSide[1] = Math.ceil(self.data.verticalPadCount());
			self.padsPerSide[2] = self.padsPerSide[0];
			self.padsPerSide[3] = self.padsPerSide[1];

		break;

		case IpcDataModel.ChipArrayFourSideFlatConcaveData:
			self.sideCount = 4;

			self.padsPerSide[0] = Math.ceil((self.data.horizontalPadCount() - 2) / 2);
			self.padsPerSide[1] = 1;
			self.padsPerSide[2] = self.padsPerSide[0];
			self.padsPerSide[3] = self.padsPerSide[1];

		break;

		case IpcDataModel.Sot23Data:
		case IpcDataModel.SotflData:
			self.sideCount = 2;

			self.padsPerSide[0] = Math.ceil(self.data.horizontalPadCount() / 2);
			self.padsPerSide[1] = self.data.horizontalPadCount() - self.padsPerSide[0];

		break;

		case IpcDataModel.Sot223Data:
		case IpcDataModel.ToDpakData:
		case IpcDataModel.Dfn3Data:
			self.sideCount = 2;

			self.padsPerSide[0] = self.data.horizontalPadCount() - 1;
			self.padsPerSide[1] = 1;
		break;

		case IpcDataModel.Sot143Data:
		case IpcDataModel.Dfn4Data:
			self.sideCount = 2;

			self.padsPerSide[0] = 2;
			self.padsPerSide[1] = 2;
		break;

		default:
		break;
	}

	self.setComponentPadGoals();
};

/**
 * Function to calculate IPC footprint pad size and center to center distance.
 * @param  {boolean} enableUpdateHeelGoal If the flag is true update heel goal for the component.
 */
Component.prototype.calculatePadsGeometry = function(enableUpdateHeelGoal) {
	var self = this;

	//////////////////////////////////
	// Pad size calculation for BGA //
	//////////////////////////////////
	
	if(self.data.constructor === IpcDataModel.BgaData) {
		//if minimum ball diamter is zero return
		if(self.data.padWidthMin() === 0) {
			return;
		}

		//note: pad width and length are same for BGA. Either we can use width or length.
		var ballDiameterIdx = (self.data.padWidthMin() + self.data.padWidthMax()) / 2;
		var lookUpEntry = (self.data.terminalType() === ComponentAttribute.bgaTerminalType["Collapsing_Ball"])
						? IpcRules.padGoal.bga.collapsible.ballDiameterLookupTable[ballDiameterIdx]:
						IpcRules.padGoal.bga.nonCollapsible.ballDiameterLookupTable[ballDiameterIdx];

		//if lookup entry not found or nominal ball diamter is not present in the keys, find nearest integer less than this in the range where it lies
		if(!lookUpEntry) {
			var range = (self.data.terminalType() === ComponentAttribute.bgaTerminalType["Collapsing_Ball"])
					? IpcRules.padGoal.bga.collapsible.range:
					IpcRules.padGoal.bga.nonCollapsible.range;

			//check min and max
			if(ballDiameterIdx < range[0][0]) {
				ballDiameterIdx = range[0][0];
			} else if(ballDiameterIdx > range[range.length - 1][1]) {
				ballDiameterIdx = range[range.length - 1][1];
			} else {
				for(var i = 0, l = range.length; i < l; i++) {
					var r = range[i];

					if(ballDiameterIdx > r[0] && ballDiameterIdx < r[1]) {
						ballDiameterIdx = r[0];
						break;
					}
				}
			}

			lookUpEntry = (self.data.terminalType() === ComponentAttribute.bgaTerminalType["Collapsing_Ball"])
						? IpcRules.padGoal.bga.collapsible.ballDiameterLookupTable[ballDiameterIdx]:
						IpcRules.padGoal.bga.nonCollapsible.ballDiameterLookupTable[ballDiameterIdx];
		}

		/*
			lookUpEntry[0] - percentage change
			lookUpEntry[1] - variation (+)
			lookUpEntry[2] - round off to increment
		 */
		self.X = Math.round((ballDiameterIdx * (1 + lookUpEntry[0])) / lookUpEntry[2]) * lookUpEntry[2] + lookUpEntry[1];
		self.Y = self.X;

		return;
	}

	var sMax, sMin, tTol, sTol, lTol;

	tTol = IPCSmdFootprintCalculator.range(self.padWidthMax, self.padWidthMin);

	/*
		Pad size calculation is different for aluminum electrolytic component than others.
		For this component there are optional parameters.
        Any two of the following three parameters can be supplied in the input. If three parameters are supplied L and S take preference.
        Optional params: pad length (T), lead-lead span (L), center gap between two opposite terminals (S).

        Note: In any one of the combination fooprint pad size may slightly differ.
    */
	if(self.data.constructor === IpcDataModel.AluminumElectrolyticData ||
		self.data.constructor === IpcDataModel.CrystalData) {
		//case I: Normal case, pad length (T) and span (L) are given and S is set to zero
		if(self.data.optionalDimension() == ComponentAttribute.optionalDimensionInput["Optional_D2"]) {
			sMax = IPCSmdFootprintCalculator.sMax(self.leadToLeadSpanMax, self.padWidthMin);
			sMin = IPCSmdFootprintCalculator.sMin(self.leadToLeadSpanMin, self.padWidthMax);
			sTol = IPCSmdFootprintCalculator.range(sMax, sMin);
		} else {
			//case II: pad length (T) and center gap between two terminals (S) are given as input
			if(self.data.optionalDimension() == ComponentAttribute.optionalDimensionInput["Optional_D"]) {
				sMin = self.data.terminalGapMin();
				sMax = self.data.terminalGapMax();

				//update lead-lead span
				var leadToLeadSpanMin = sMin + 2 * self.padWidthMin;
				var leadToLeadSpanMax = sMax + 2 * self.padWidthMax;
				var leadToLeadSpanMean = (leadToLeadSpanMin + leadToLeadSpanMax) / 2;

				sTol = IPCSmdFootprintCalculator.range(sMax, sMin);

				//lead-lead span tolerance
				lTol = IPCSmdFootprintCalculator.lTolAE(sTol, tTol);

				//update span min/max
				self.leadToLeadSpanMin = leadToLeadSpanMean - lTol / 2;
				self.leadToLeadSpanMax = leadToLeadSpanMean + lTol / 2;
			}

			//Case III: span (L) and S are given
			else if(self.data.optionalDimension() == ComponentAttribute.optionalDimensionInput["Optional_L"]) {
				//console.log('Case III');
				var tMax = self.leadToLeadSpanMax - self.data.terminalGapMin();
				var tMin = self.leadToLeadSpanMin - self.data.terminalGapMax();
				var tMean = (tMax + tMin) / 2;

				sMin = self.data.terminalGapMin();
				sMax = self.data.terminalGapMax();

				sTol = IPCSmdFootprintCalculator.range(sMax, sMin);
				lTol = self.leadToLeadSpanMax - self.leadToLeadSpanMin;

				tTol = IPCSmdFootprintCalculator.tTolAE(sTol, lTol);

				//update pad size
				self.padWidthMin = (tMean - tTol) / 2;
				self.padWidthMax = (tMean + tTol) / 2;
			}
		}
	} else if(self.data.constructor === IpcDataModel.OscillatorCornerConcaveData) {
		sMin = self.data.terminalGapMin;
		sMax = self.data.terminalGapMax;
	} else {
		sMax = IPCSmdFootprintCalculator.sMax(self.leadToLeadSpanMax, self.padWidthMin);
		sMin = IPCSmdFootprintCalculator.sMin(self.leadToLeadSpanMin, self.padWidthMax);
		sTol = IPCSmdFootprintCalculator.range(sMax, sMin);
	}

	//internal calculation for IPC footprint
	//refer design requirements (section 3) in IPC 7351(B) doc for the formulas
	var lRange = IPCSmdFootprintCalculator.range(self.leadToLeadSpanMax, self.leadToLeadSpanMin);
	var tRange = IPCSmdFootprintCalculator.range(self.padWidthMax, self.padWidthMin);
	var wRange = IPCSmdFootprintCalculator.range(self.padHeightMax, self.padHeightMin);

	var sTolRMS = IPCSmdFootprintCalculator.sTolRMS(lRange, tRange);
	var sDiff = IPCSmdFootprintCalculator.range(sTol, sTolRMS);

	//update actual S
	var sMaxActual, sMinActual;

	if(self.data.constructor === IpcDataModel.AluminumElectrolyticData ||
		self.data.constructor === IpcDataModel.CrystalData) {
		if(self.data.terminalGapMax() === 0) {
			//normal case
			sMaxActual = IPCSmdFootprintCalculator.sMaxActual(sMax, sDiff);
			sMinActual = IPCSmdFootprintCalculator.sMinActual(sMin, sDiff);
		} else {
			//directly assign S to actual SMin and Smax
			sMaxActual = sMax;
			sMinActual = sMin;
		}
	} else if(self.data.constructor === IpcDataModel.OscillatorCornerConcaveData) {
		sMaxActual = sMax;
		sMinActual = sMin;
	} else { //for others
		sMaxActual = IPCSmdFootprintCalculator.sMaxActual(sMax, sDiff);
		sMinActual = IPCSmdFootprintCalculator.sMinActual(sMin, sDiff);
	}

	//lRange is considered 0 for Corner Concave Oscillator (TODO: explaination)
	var toeTol = IPCSmdFootprintCalculator.toeTolerance(((self.data.constructor === IpcDataModel.OscillatorCornerConcaveData) ? 0 : lRange), self.data.fabricationTolerance(), self.data.placementTolerance());
	var zMax = IPCSmdFootprintCalculator.zMax(self.leadToLeadSpanMin, self.toeGoal, toeTol);
	var heelTol = IPCSmdFootprintCalculator.heelTolerance(sMaxActual, sMinActual, self.data.fabricationTolerance(), self.data.placementTolerance());

	//update heel goal depends on Smin, tTol, bodyWidthMax
	//refer IPC 7531B for details
	if(self.data.constructor === IpcDataModel.SoicSopData ||
		self.data.constructor === IpcDataModel.CfpData ||
		self.data.constructor === IpcDataModel.QfpData ||
		self.data.constructor === IpcDataModel.CqfpData ||
		self.data.constructor === IpcDataModel.Sot23Data ||
		self.data.constructor === IpcDataModel.Sot223Data ||
		self.data.constructor === IpcDataModel.ToDpakData ||
		self.data.constructor === IpcDataModel.Sot143Data ||
		self.data.constructor === IpcDataModel.SodData) {
		if(enableUpdateHeelGoal && tTol <= 500) {
			if(sMin <= self.data.bodyWidthMax()) {
				var updatedGullWingHeelFilletMaxMedMinGT = [250, 150, 50];
				self.heelGoal = updatedGullWingHeelFilletMaxMedMinGT[self.data.densityLevel()];
			} else {
				self.heelGoal = IpcRules.padGoal.gullWing.heelFilletMaxMedMinGT[self.data.densityLevel()];
			}
		} else {
			//TODO: Need to check
            //heel must be equal to side fillet for TO, DPAK packages
			if(self.data.constructor === IpcDataModel.ToDpakData) {
				self.heelGoal = self.sideGoal;
			} else {
				self.heelGoal = IpcRules.padGoal.gullWing.heelFilletMaxMedMinGT[self.data.densityLevel()];
			}
		}
	} else if(self.data.constructor === IpcDataModel.QfnData) {
		//TODO: rule for QFN
	}

	//TODO: Need to check for periphery update for DFN packages
	var gMin = IPCSmdFootprintCalculator.gMin(sMaxActual, self.heelGoal, heelTol);
	var sideTol = IPCSmdFootprintCalculator.sideTol(wRange, self.data.fabricationTolerance(), self.data.placementTolerance());
	var yRef = IPCSmdFootprintCalculator.yRef(self.padHeightMin, self.sideGoal, sideTol);

	//IPC calculated pad width
	self.X = IPCSmdFootprintCalculator.X(zMax, gMin);

	//IPC calculated pad height
	self.Y = yRef;

	//IPC calculated pad center to center distance
	self.C = IPCSmdFootprintCalculator.C(zMax, gMin);

	//IPC pad goals
	self.toeMin = IPCSmdFootprintCalculator.toeMin(self.X, self.C, toeTol, self.leadToLeadSpanMin);
	self.toeMax = IPCSmdFootprintCalculator.toeMax(toeTol, self.toeMin);

	self.heelMin = IPCSmdFootprintCalculator.heelMin(sMaxActual, heelTol, gMin);
	self.heelMax = IPCSmdFootprintCalculator.heelMax(self.heelMin, heelTol);

	self.sideMin = IPCSmdFootprintCalculator.sideMin(self.Y, self.padHeightMin, sideTol);
	self.sideMax = IPCSmdFootprintCalculator.sideMax(self.sideMin, sideTol);
};